import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/api/client";
import { CONTRACT_STATUS, AI_STATUS, CONTRACT_SOURCE, fmtDateTime } from "@/lib/domain";
import { Chip } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, FileText, Upload, ExternalLink } from "lucide-react";
import { toast, Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContractDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [contract, setContract] = useState(null);
  const [fileTokens, setFileTokens] = useState({});
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    const r = await api.get(`/contracts/${id}`);
    setContract(r.data);
    const tokenEntries = await Promise.all(
      (r.data.files || []).map(async (f) => {
        try {
          const t = await api.get(`/files/token/${f.storage_path}`);
          return [f.storage_path, t.data.token];
        } catch {
          return [f.storage_path, null];
        }
      })
    );
    setFileTokens(Object.fromEntries(tokenEntries));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const fileUrl = (path) => {
    const token = fileTokens[path];
    return token ? `${BACKEND_URL}/api/files/${path}?auth=${encodeURIComponent(token)}` : null;
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/contracts/${id}`, { status });
      toast.success("Статус обновлён");
      load();
    } catch (e) {
      toast.error("Ошибка: " + formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const uploadVersion = async (file) => {
    if (!file) return;
    const nextVersion = (contract.files || []).length + 1;
    const label = window.prompt("Название версии (например: Nachtrag 1)", `Nachtrag ${nextVersion - 1}`);
    if (label === null) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("label", label || "Vertrag");
      await api.post(`/contracts/${id}/files`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Файл загружен");
      load();
    } catch (e) {
      toast.error("Ошибка: " + formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (!contract) return null;

  const status = CONTRACT_STATUS[contract.status] || { label: contract.status, cls: "" };
  const ai = AI_STATUS[contract.ai_status];

  return (
    <div className="space-y-6 max-w-3xl">
      <Toaster theme="dark" position="top-right" />
      <button onClick={() => nav("/contracts")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200">
        <ArrowLeft size={16} /> Договоры
      </button>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl">{contract.title}</h1>
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
            <span>{CONTRACT_SOURCE[contract.source] || contract.source}</span>
            {contract.source_ref && <span>· {contract.source_ref}</span>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Chip className={status.cls}>{status.label}</Chip>
          {ai && ai.label !== "—" && <Chip className={ai.cls}>{ai.label}</Chip>}
        </div>
      </div>

      <Card className="p-5 bg-[#131924] border-[#212b3d] space-y-4">
        <div>
          <Label>Статус</Label>
          <Select value={contract.status} onValueChange={setStatus}>
            <SelectTrigger data-testid="contract-status-select" className="bg-[#0f1521] border-[#212b3d] mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#131924] border-[#212b3d] text-slate-100">
              {Object.entries(CONTRACT_STATUS).map(([value, meta]) => (
                <SelectItem key={value} value={value}>{meta.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs text-slate-500">
          <div>Клиент: {contract.client_id || "—"}</div>
          <div>Объект: {contract.object_id || "—"}</div>
        </div>
      </Card>

      <Card className="p-5 bg-[#131924] border-[#212b3d]">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-semibold">Версии договора</div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              data-testid="contract-version-file-input"
              className="hidden"
              onChange={(e) => uploadVersion(e.target.files?.[0])}
            />
            <Button
              size="sm"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            >
              <Upload size={14} /> {uploading ? "Загрузка..." : "Добавить версию"}
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          {(contract.files || []).length === 0 && (
            <div className="text-center text-slate-500 py-6 text-sm">
              <FileText className="mx-auto mb-2 opacity-50" size={24} />
              Файлов ещё нет.
            </div>
          )}
          {(contract.files || []).map((f) => {
            const url = fileUrl(f.storage_path);
            return (
              <div key={f.storage_path} data-testid={`contract-file-${f.version_number}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-[#0f1521] border border-[#212b3d]">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-200 truncate">v{f.version_number} · {f.label}</div>
                  <div className="text-[11px] text-slate-500 truncate">{f.filename} · {fmtDateTime(f.uploaded_at)}</div>
                </div>
                {url ? (
                  <a href={url} target="_blank" rel="noreferrer" className="text-amber-400 hover:text-amber-300 flex items-center gap-1 text-xs shrink-0">
                    Открыть <ExternalLink size={12} />
                  </a>
                ) : (
                  <span className="text-xs text-slate-600 shrink-0">...</span>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
